import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Zap } from 'lucide-react';
import { getImageRange, getDailyImages } from '../../camera/api/iframeConfig.api';
import { getObraByFeedID } from '../../obras/api/obras.api';
import { getObraTimeZone } from '../../videoEditor/utils/obraTimeZone';
import { photoUrl, miniaturasCaidas } from '../../camera/components/IADashboard/fotos';

// "Timelapse rápido": mientras el video de verdad no está (se arma o el motor falló), el
// navegador arma uno al instante con las fotos de la obra: una foto por día cerca del mediodía desde el inicio
// (o desde el comienzo del período) hasta hoy; si son muchos días, cada N días (≈60-110 cuadros), y si son pocos,
// hasta 4 fotos por día. Se reproduce como un video (play/pausa, velocidad, barra de tiempo, fecha arriba).
// Fotos: miniatura de 1280 si el backend tiene /api/fotos/mini (fotos.js); si no, la URL firmada de siempre.
// Se baja de a pocos (4 días a la vez) y arranca a reproducir con lo que ya llegó.

const TZ_AR = 'America/Argentina/Buenos_Aires';
const MAX_CUADROS = 110;
const MAX_CUADROS_ORIGINAL = 48; // sin miniaturas (fotos 4K)
const MAX_POR_DIA = 4;
const CONCURRENCIA = 4;
const FPS = 8;
const VELOCIDADES = [1, 2, 0.5];
const HORAS_OBJETIVO = { 1: [13], 2: [11, 15], 3: [10, 13, 16], 4: [9, 11.5, 14, 16.5] };
const DIA_MS = 864e5;

const diaAR = (d, tz = TZ_AR) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const horaAR = (d, tz = TZ_AR) => {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(d);
  const v = (t) => Number(p.find((x) => x.type === t)?.value || 0);
  return v('hour') + v('minute') / 60;
};
const fmtFecha = (d, tz = TZ_AR) => d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: tz });

// Fecha de la foto: la que manda el endpoint (S3 lastModified, un instante real). El nombre del archivo no se usa:
// lleva el reloj de la cámara, que puede estar mal configurado.
const fechaDe = (it) => {
  const d = it?.date != null ? new Date(it.date) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
};
// Baja la foto antes de mostrarla (sin decode(): en pestañas ocultas puede no terminar nunca). Tope 25 s.
const precargar = (url) => new Promise((ok, mal) => {
  const im = new Image();
  const t = setTimeout(() => mal(new Error('lenta')), 25000);
  im.onload = () => { clearTimeout(t); ok(im); };
  im.onerror = () => { clearTimeout(t); mal(new Error('no cargó')); };
  im.src = url;
});
// Clave S3 a partir de la URL firmada (para pedir la miniatura).
const claveDe = (url) => {
  try {
    const u = new URL(url);
    return /amazonaws\.com$/.test(u.hostname) ? decodeURIComponent(u.pathname.replace(/^\//, '')) : null;
  } catch (e) { return null; }
};

/** Días (YYYY-MM-DD, hora de la obra; AR por defecto) del plan y cuántas fotos por día. Exportado para tests. */
export function planDias(inicio, fin, max = MAX_CUADROS, maxPorDia = MAX_POR_DIA, tz = TZ_AR) {
  const dias = [];
  for (let t = inicio.getTime(); t <= fin.getTime() + DIA_MS / 2; t += DIA_MS) {
    const d = diaAR(new Date(t), tz);
    if (dias[dias.length - 1] !== d) dias.push(d);
  }
  const ultimo = diaAR(fin, tz);
  if (dias[dias.length - 1] !== ultimo) dias.push(ultimo);
  const paso = Math.max(1, Math.ceil(dias.length / max));
  const elegidos = dias.filter((_, k) => k % paso === 0);
  if (elegidos[elegidos.length - 1] !== ultimo) elegidos.push(ultimo);
  const porDia = Math.max(1, Math.min(maxPorDia, Math.floor(max / Math.max(1, elegidos.length))));
  return { dias: elegidos, porDia };
}

/** De las fotos de un día, las más cercanas a las horas objetivo (sin repetir), en orden. Exportado para tests. */
export function elegirDelDia(items, porDia, tz = TZ_AR) {
  const fotos = (Array.isArray(items) ? items : [])
    .map((it) => { const url = typeof it === 'string' ? it : it?.url; return url ? { url, fecha: fechaDe(it) } : null; })
    .filter((f) => f && f.fecha);
  const usadas = new Set();
  const out = [];
  for (const h of HORAS_OBJETIVO[porDia] || HORAS_OBJETIVO[1]) {
    let mejor = null;
    for (const f of fotos) {
      if (usadas.has(f.url)) continue;
      const dif = Math.abs(horaAR(f.fecha, tz) - h);
      if (!mejor || dif < mejor.dif) mejor = { f, dif };
    }
    if (mejor && mejor.dif <= 4) { usadas.add(mejor.f.url); out.push(mejor.f); }
  }
  return out.sort((a, b) => a.fecha - b.fecha);
}

// Lo ya armado queda en memoria por obra + período: al pasar del timelapse rápido al del Editor (o volver a un chip)
// no se vuelve a pedir nada y las fotos ya están en la caché del navegador.
const MEMORIA = new Map();

function armar(feedId, desdeMs, avisar, tz = TZ_AR) {
  const clave = `${feedId}|${tz}|${desdeMs ? diaAR(new Date(desdeMs), tz) : 'todo'}|${diaAR(new Date(), tz)}`;
  let m = MEMORIA.get(clave);
  if (m) { m.oyentes.add(avisar); avisar(m); return m; }
  m = { cuadros: [], total: 0, terminado: false, sinFotos: false, oyentes: new Set([avisar]) };
  MEMORIA.set(clave, m);
  if (MEMORIA.size > 12) MEMORIA.delete(MEMORIA.keys().next().value);
  const emitir = () => m.oyentes.forEach((f) => f(m));
  if (process.env.NODE_ENV === 'development') { window.__tlRapido = window.__tlRapido || {}; window.__tlRapido[clave] = m; } // para inspeccionar en local

  (async () => {
    const fin = new Date();
    let inicio = desdeMs ? new Date(desdeMs) : null;
    let ultima = fin;
    try {
      const r = await getImageRange(feedId, { period: 'COMPLETO' });
      const primera = new Date(r?.firstImage?.date || r?.rangeStart);
      const ult = new Date(r?.lastImage?.date || r?.rangeEnd);
      if (!Number.isNaN(primera.getTime()) && (!inicio || primera > inicio)) inicio = primera;
      if (!Number.isNaN(ult.getTime()) && ult < fin) ultima = ult;
    } catch (e) { /* sin rango: se usa el período pedido */ }
    if (!inicio || Number.isNaN(inicio.getTime())) inicio = new Date(fin.getTime() - 365 * DIA_MS);
    if (ultima < inicio) ultima = fin;
    // Miniaturas: se prueba con una foto del primer día. Si el backend no las tiene, van las originales (4K, 1-3 MB):
    // entonces menos cuadros y una sola foto por día, para no bajar cientos de MB.
    const primerDia = diaAR(inicio, tz);
    let itemsPrimero = null;
    try { itemsPrimero = await getDailyImages(feedId, primerDia); } catch (e) { /* sin fotos ese día */ }
    const muestra = elegirDelDia(itemsPrimero, 1, tz)[0];
    const k0 = muestra && claveDe(muestra.url);
    let usarMini = false;
    if (k0 && !miniaturasCaidas()) {
      usarMini = await Promise.race([
        photoUrl({ s3: k0 }, { w: 1280, prioridad: 'alta' }).then((u) => precargar(u)).then(() => !miniaturasCaidas()),
        new Promise((ok) => setTimeout(() => ok(false), 5000)),
      ]).catch(() => false);
    }
    const { dias, porDia } = usarMini ? planDias(inicio, ultima, MAX_CUADROS, MAX_POR_DIA, tz) : planDias(inicio, ultima, MAX_CUADROS_ORIGINAL, 1, tz);
    m.total = dias.length * porDia;
    m.plan = { dias: dias.length, porDia, usarMini };
    const estado = dias.map(() => ({ hecho: false, fotos: [] }));
    const urlDe = (f) => {
      const k = usarMini && claveDe(f.url);
      return k ? photoUrl({ s3: k }, { w: 1280 }).catch(() => f.url) : Promise.resolve(f.url);
    };
    const publicar = () => {
      const cuadros = [];
      for (const d of estado) { if (!d.hecho) break; cuadros.push(...d.fotos); }
      m.cuadros = cuadros;
      emitir();
    };

    let sig = 0;
    const trabajador = async () => {
      while (sig < dias.length) {
        const k = sig++;
        let items = dias[k] === primerDia && itemsPrimero ? itemsPrimero : [];
        if (!items.length) {
          try { items = await getDailyImages(feedId, dias[k]); } catch (e) { /* día sin fotos */ }
        }
        const elegidas = elegirDelDia(items, porDia, tz);
        const listas = await Promise.all(elegidas.map(async (f) => {
          let url = await urlDe(f);
          try { await precargar(url); } catch (e) {
            if (url === f.url) return null;
            url = f.url;
            try { await precargar(url); } catch (e2) { return null; }
          }
          return { url, fecha: f.fecha };
        }));
        estado[k] = { hecho: true, fotos: listas.filter(Boolean) };
        publicar();
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCIA }, trabajador));
    m.terminado = true;
    m.sinFotos = m.cuadros.length === 0;
    if (m.sinFotos) MEMORIA.delete(clave); // la próxima vez se vuelve a probar
    emitir();
  })();
  return m;
}

/**
 * feedId; desde = Date|ms de inicio (null = desde la primera foto); rotulo = texto chico arriba a la derecha;
 * extra = algo debajo del rótulo (barra de avance real); imgStyle/onImgLoad = para cámaras rotadas.
 */
const TimelapseRapido = ({ feedId, desde = null, rotulo, extra, imgStyle, onImgLoad, className = '' }) => {
  const [datos, setDatos] = useState({ cuadros: [], total: 0, terminado: false, sinFotos: false });
  const [i, setI] = useState(0);
  const [jugando, setJugando] = useState(true);
  const [vel, setVel] = useState(0);
  const desdeMs = desde ? new Date(desde).getTime() : null;
  const primeraCarga = useRef(false);

  // Zona de la obra (fallback AR): se espera a saberla para no armar el timelapse dos veces.
  const [tz, setTz] = useState(null);
  useEffect(() => {
    let vivo = true;
    setTz(null);
    getObraByFeedID(feedId)
      .then((obra) => getObraTimeZone(obra))
      .catch(() => null)
      .then((z) => { if (vivo) setTz(z || TZ_AR); });
    return () => { vivo = false; };
  }, [feedId]);

  useEffect(() => {
    if (!tz) return undefined;
    let vivo = true;
    setI(0);
    primeraCarga.current = false;
    const avisar = (m) => { if (vivo) setDatos({ cuadros: m.cuadros, total: m.total, terminado: m.terminado, sinFotos: m.sinFotos }); };
    const m = armar(feedId, desdeMs, avisar, tz);
    return () => { vivo = false; m.oyentes.delete(avisar); };
  }, [feedId, desdeMs, tz]);

  const n = datos.cuadros.length;
  useEffect(() => {
    if (!jugando || n === 0) return undefined;
    const fin = i >= n - 1;
    if (fin && !datos.terminado) return undefined; // esperando que lleguen más fotos
    const ms = fin ? 1500 : 1000 / (FPS * VELOCIDADES[vel]);
    const t = setTimeout(() => setI((x) => (x >= n - 1 ? 0 : x + 1)), ms);
    return () => clearTimeout(t);
  }, [jugando, i, n, datos.terminado, vel]);

  const actual = datos.cuadros[Math.min(i, Math.max(0, n - 1))];
  const estilo = imgStyle || { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
  const cargado = datos.total ? Math.min(100, (100 * n) / datos.total) : 0;

  return (
    <div className={`absolute inset-0 ${className}`}>
      {actual ? (
        <img
          src={actual.url}
          alt={`Obra el ${fmtFecha(actual.fecha, tz || TZ_AR)}`}
          style={estilo}
          draggable={false}
          onLoad={(e) => { if (!primeraCarga.current && onImgLoad) { primeraCarga.current = true; onImgLoad(e); } }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <div className="absolute inset-0 animate-pulse bg-gray-300/60" />
          <p className="relative text-sm text-gray-600">
            {datos.sinFotos ? 'Todavía no hay fotos para armar el timelapse.' : 'Preparando el timelapse…'}
          </p>
        </div>
      )}

      {actual && (
        <div className="absolute left-2 top-2 sm:left-3 sm:top-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white tabular-nums backdrop-blur-sm">
          {fmtFecha(actual.fecha, tz || TZ_AR)}
        </div>
      )}

      {(rotulo || extra) && (
        <div className="absolute left-2 top-10 max-w-[85%] sm:left-auto sm:right-3 sm:top-3 sm:max-w-[55%] z-10 rounded-xl bg-black/60 px-2.5 py-1.5 text-white backdrop-blur-sm">
          {rotulo && (
            <p className="flex items-start gap-1.5 text-[11px] leading-snug">
              <Zap className="mt-px h-3 w-3 shrink-0 text-amber-300" />
              <span>{rotulo}</span>
            </p>
          )}
          {extra}
        </div>
      )}

      {n > 0 && (
        <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-2.5 pb-2 pt-6 sm:px-3">
          <button
            type="button"
            onClick={() => setJugando((j) => !j)}
            className="shrink-0 rounded-full p-1 [@media(pointer:coarse)]:p-2.5 text-white hover:bg-white/15"
            aria-label={jugando ? 'Pausar' : 'Reproducir'}
          >
            {jugando ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </button>
          <div className="relative flex-1 h-5 flex items-center">
            <div className="absolute inset-x-0 h-1.5 rounded-full bg-white/25" />
            {!datos.terminado && <div className="absolute left-0 h-1.5 rounded-full bg-white/40" style={{ width: `${cargado}%` }} />}
            <input
              type="range"
              min={0}
              max={Math.max(0, n - 1)}
              value={Math.min(i, n - 1)}
              onChange={(e) => { setJugando(false); setI(Number(e.target.value)); }}
              className="relative w-full cursor-pointer accent-white"
              style={datos.terminado ? undefined : { width: `${Math.max(4, cargado)}%` }}
              aria-label="Fecha del timelapse"
            />
          </div>
          {!datos.terminado && jugando && i >= n - 1 && (
            <span className="shrink-0 text-[10px] text-white/80">cargando fotos…</span>
          )}
          <button
            type="button"
            onClick={() => setVel((v) => (v + 1) % VELOCIDADES.length)}
            className="shrink-0 rounded-md bg-white/15 px-1.5 py-0.5 text-xs font-semibold text-white tabular-nums hover:bg-white/25"
            aria-label="Velocidad"
          >
            {VELOCIDADES[vel]}×
          </button>
        </div>
      )}
    </div>
  );
};

export default TimelapseRapido;
